import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./OfficialComplaintStatus.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT
  ? (API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`)
  : "/api";

const ALLOWED_MODULES = [1, 2, 3, 5, 7, 13, 15, 16];

function formatDateTime(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = String(dt.getFullYear()).slice(-2);
  const hours = String(dt.getHours()).padStart(2, "0");
  const mins = String(dt.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${mins}`;
}

function resolveAttachmentUrl(pathValue) {
  const raw = String(pathValue || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/")) return `${API_ROOT}${raw}`;
  return `${API_ROOT}/${raw.replace(/^\/+/, "")}`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export default function OfficialComplaintStatus() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const deptId = Number.parseInt(localUser?.deptid, 10);
  const moduleId = Number.parseInt(localUser?.moduleId, 10);

  useEffect(() => {
    const loadStatus = async () => {
      if (!deptId || !moduleId || !ALLOWED_MODULES.includes(moduleId)) {
        setError("Unauthorized access Or Session Timed out.");
        setLoading(false);
        return;
      }

      try {
        setError("");
        setLoading(true);
        const query = new URLSearchParams({ deptId: String(deptId), moduleId: String(moduleId) });
        const response = await fetch(`${API_BASE}/complaints/office-status?${query.toString()}`);
        const payload = await response.json();

        if (!response.ok) {
          throw new Error(payload?.message || "Failed to fetch complaint status");
        }

        setRows(Array.isArray(payload?.data) ? payload.data : []);
      } catch (err) {
        setRows([]);
        setError(err.message || "Failed to fetch complaint status");
      } finally {
        setLoading(false);
      }
    };

    loadStatus();
  }, [deptId, moduleId]);

  const openPrint = (ticketId) => {
    const query = new URLSearchParams({ deptId: String(deptId || ""), moduleId: String(moduleId || "") });
    const url = `${API_BASE}/complaints/print/${encodeURIComponent(ticketId)}?${query.toString()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openAttachment = (docPath) => {
    const resolved = resolveAttachmentUrl(docPath);
    if (!resolved) {
      window.alert("No attachment found.");
      return;
    }
    window.open(resolved, "_blank", "noopener,noreferrer");
  };

  const openProgress = async (ticketId) => {
    const childWindow = window.open(
      "",
      `office_ticket_progress_${ticketId}`,
      "width=1020,height=720,left=80,top=60,resizable=yes,scrollbars=yes"
    );

    if (!childWindow) {
      window.alert("Popup blocked. Please allow popups for this site.");
      return;
    }

    childWindow.document.write("<html><head><title>Progress Details</title></head><body style='font-family:Arial,sans-serif;padding:16px;'><h3>Progress Details</h3><p>Loading...</p></body></html>");
    childWindow.document.close();

    try {
      const query = new URLSearchParams({ deptId: String(deptId || ""), moduleId: String(moduleId || "") });
      const response = await fetch(`${API_BASE}/complaints/progress/${encodeURIComponent(ticketId)}?${query.toString()}`);
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.message || "Failed to fetch complaint progress");
      }

      const progressData = Array.isArray(payload?.data) ? payload.data : [];
      const rowsHtml = progressData.length
        ? progressData.map((row, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${escapeHtml(row.DOWPLabel || "-")}</td>
              <td>${escapeHtml(row.StatusLabel || row.Status || "-")}</td>
              <td>${escapeHtml(row.Resolver || "-")}</td>
              <td>${escapeHtml(row.Department || "-")}</td>
              <td>${escapeHtml(row.ForwardedToName || "-")}</td>
              <td>${escapeHtml(row.Remark || "-")}</td>
            </tr>
          `).join("")
        : `<tr><td colspan="7" style="text-align:center;color:#666;">No progress found.</td></tr>`;

      childWindow.document.open();
      childWindow.document.write(`
        <html>
          <head>
            <title>Progress Details</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 16px; }
              h3 { margin: 0 0 12px; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #333; padding: 6px; vertical-align: top; }
              thead th { background: #008080; color: white; }
            </style>
          </head>
          <body>
            <h3>Progress Details (ComplaintID ${escapeHtml(ticketId)})</h3>
            <table>
              <thead>
                <tr>
                  <th>SNo</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Resolver</th>
                  <th>Department</th>
                  <th>Forwarded To</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </body>
        </html>
      `);
      childWindow.document.close();
    } catch (err) {
      childWindow.document.open();
      childWindow.document.write(`
        <html>
          <head><title>Progress Details</title></head>
          <body style="font-family:Arial,sans-serif;padding:16px;">
            <h3>Progress Details</h3>
            <p style="color:#b00020;">${escapeHtml(err.message || "Failed to fetch complaint progress")}</p>
          </body>
        </html>
      `);
      childWindow.document.close();
    }
  };

  return (
    <section className="container-fluid">
      <div style={{ textAlign: "left", marginTop: "10px" }}>
        <Link className="btn btn-primary" to="/official-complaints/new">
          New Departmental Complaint
        </Link>
      </div>

      <h3 className="mt-3 mb-3">Complaint Status Raised by Department</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-bordered table-striped official-complaints-table">
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th>SNo</th>
              <th>ComplaintType</th>
              <th>Complaint Description</th>
              <th>Remarks</th>
              <th>Area Type</th>
              <th>HNo/Dept</th>
              <th>DOC</th>
              <th>Status</th>
              <th>ComplaintID</th>
              <th>AssignedTo</th>
              <th>Print</th>
              <th>Doc</th>
              <th>Progress</th>
              <th>Complaintant</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={14} className="text-center text-muted">Loading...</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={14} className="text-center text-muted">No records found.</td>
              </tr>
            )}
            {!loading && rows.map((row, index) => (
              <tr key={`${row.TicketID}-${index}`}>
                <td>{index + 1}</td>
                <td>{row.ComplaintCategory || "-"}</td>
                <td>{row.ComplaintDescription || "-"}</td>
                <td>{row.Description || "-"}</td>
                <td>{row.ResiType || "-"}</td>
                <td>{row.ResidenceID || "-"}</td>
                <td>{formatDateTime(row.DOC)}</td>
                <td>{row.StatusLabel || "-"}</td>
                <td>{row.TicketID || "-"}</td>
                <td>{row.EmpName || "-"}</td>
                <td><button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openPrint(row.TicketID)}>Print</button></td>
                <td><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openAttachment(row.SupportingDocUrl)}>View</button></td>
                <td><button type="button" className="btn btn-sm btn-outline-info" onClick={() => openProgress(row.TicketID)}>Progress</button></td>
                <td>{row.Complaintant || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
